import { z } from "zod";
import { memoryRolloutModeSchema } from "./memory-policy.js";
import { memoryTraceLaneSchema, memoryTraceSourceSchema } from "./memory-trace.js";
import { metricDistributionSchema, timeframeSchema } from "./metrics.js";
const countSchema = z.number().int().nonnegative();
const metricCodeSchema = z.string().regex(/^[a-z][a-z0-9_]{0,79}$/u);
export const memoryMetricAttemptOutcomeSchema = z.enum([
    "admitted",
    "no_memory",
    "clarification_required",
]);
const tracePhaseSchema = z.enum([
    "retrieval",
    "authorisation",
    "admission",
    "surface",
    "application",
]);
const traceOutcomeSchema = z.enum([
    "retrieved",
    "failed",
    "authorised",
    "rejected",
    "admitted",
    "not_evaluated",
    "surfaced",
    "omitted",
    "applied",
]);
export const memoryMetricsDashboardSchema = z.object({
    scope: z.object({
        generatedAt: z.number(),
        from: z.number(),
        to: z.number(),
        timeframe: timeframeSchema,
        organisationId: z.string().optional(),
    }).strict(),
    attempts: z.object({
        total: countSchema,
        outcomes: z.object({
            admitted: countSchema,
            noMemory: countSchema,
            clarificationRequired: countSchema,
        }).strict(),
        byMode: z.array(z.object({
            mode: memoryRolloutModeSchema,
            outcome: memoryMetricAttemptOutcomeSchema,
            count: countSchema,
        }).strict()),
        clarification: z.object({
            count: countSchema,
            eligible: countSchema,
            rate: z.number().min(0).max(1).optional(),
        }).strict(),
        technicalFailures: z.array(z.object({
            stage: z.string().trim().min(1).max(80),
            code: metricCodeSchema,
            count: countSchema,
        }).strict()),
    }).strict(),
    traces: z.object({
        totalEvents: countSchema,
        validEvents: countSchema,
        invalidEvents: countSchema,
        stages: z.array(z.object({
            lane: memoryTraceLaneSchema,
            source: memoryTraceSourceSchema,
            phase: tracePhaseSchema,
            outcome: traceOutcomeSchema,
            count: countSchema,
        }).strict()),
        rejectionReasons: z.array(z.object({
            lane: memoryTraceLaneSchema,
            source: memoryTraceSourceSchema,
            phase: tracePhaseSchema,
            outcome: traceOutcomeSchema,
            code: metricCodeSchema,
            count: countSchema,
        }).strict()),
    }).strict(),
    servingLatency: metricDistributionSchema,
    curator: z.object({
        calls: countSchema,
        usageCapturedCalls: countSchema,
        tokenCapturedCalls: countSchema,
        totalTokens: countSchema.optional(),
        costCapturedCalls: countSchema,
        costMicrousd: countSchema.optional(),
    }).strict(),
    lifecycle: z.object({
        corrections: countSchema,
        retractions: countSchema,
        doNotReuse: countSchema,
        restores: countSchema,
        forgets: countSchema,
        forgetEvidence: z.object({
            sourceTombstones: countSchema,
            distinctSourceMemories: countSchema,
            operationCount: countSchema,
            coverage: z.literal("durable_lifecycle_receipts"),
        }).strict(),
    }).strict(),
}).strict().superRefine((dashboard, context) => {
    const outcomeTotal = dashboard.attempts.outcomes.admitted +
        dashboard.attempts.outcomes.noMemory +
        dashboard.attempts.outcomes.clarificationRequired;
    if (outcomeTotal !== dashboard.attempts.total) {
        context.addIssue({
            code: "custom",
            path: ["attempts", "outcomes"],
            message: "Attempt outcomes must reconcile to the immutable attempt total",
        });
    }
    if (dashboard.attempts.clarification.eligible !== dashboard.attempts.total) {
        context.addIssue({
            code: "custom",
            path: ["attempts", "clarification", "eligible"],
            message: "Clarification eligibility must use the immutable attempt denominator",
        });
    }
    if (dashboard.traces.validEvents + dashboard.traces.invalidEvents !==
        dashboard.traces.totalEvents) {
        context.addIssue({
            code: "custom",
            path: ["traces"],
            message: "Valid and invalid trace counts must reconcile to total events",
        });
    }
});
